/**
 * Runtime mínimo para rodar a tela desenhada no canvas (formato dc) dentro do widget.
 *
 * - O HTML da tela usa {{buracos}}, <sc-if value> e <sc-for list as>, igual ao canvas.
 * - A classe Component (renderVals, setState, componentDidMount) é a mesma do canvas.
 * - Cada render monta a árvore nova e o morphdom aplica só a diferença no Shadow DOM,
 *   então foco, cursor do campo e posição da tela não se perdem.
 * - Eventos (onClick, onInput, onBlur...) são delegados na raiz e sobem como no React.
 */
import morphdom from 'morphdom';

type Escopo = Record<string, unknown>;
type Manipulador = (e: Event) => void;

export class DCLogic {
  props: Record<string, any>;
  state: Record<string, any> = {};
  /** preenchido pelo runtime: agenda um render no próximo microtask */
  __agendar?: () => void;
  constructor(props?: Record<string, any>) {
    this.props = props || {};
  }
  setState(parcial: Record<string, any> | ((s: Record<string, any>) => Record<string, any>)) {
    const p = typeof parcial === 'function' ? parcial(this.state) : parcial;
    // o estado muda na hora (o código do canvas lê this.state logo depois); só o desenho é agrupado
    this.state = Object.assign({}, this.state, p);
    if (this.__agendar) this.__agendar();
  }
}

export interface ComponenteDC extends DCLogic {
  renderVals(): Escopo;
  componentDidMount?(): void;
  componentWillUnmount?(): void;
}

const SVG = 'http://www.w3.org/2000/svg';
const BURACO = /\{\{\s*([^}]+?)\s*\}\}/g;
const BURACO_INTEIRO = /^\s*\{\{\s*([^}]+?)\s*\}\}\s*$/;

/** atributo onX -> evento DOM delegado */
const EVENTOS: Record<string, string> = {
  onclick: 'click',
  oninput: 'input',
  onchange: 'change',
  onblur: 'focusout',
  onfocus: 'focusin',
  onkeydown: 'keydown',
  onsubmit: 'submit',
};

function valor(expr: string, esc: Escopo): unknown {
  if (expr === 'true') return true;
  if (expr === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(expr)) return Number(expr);
  const partes = expr.split('.');
  let v: any = esc[partes[0]!];
  for (let i = 1; i < partes.length; i++) v = v == null ? undefined : v[partes[i]!];
  return v;
}

function comoTexto(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  return '';
}

function interpolar(s: string, esc: Escopo): string {
  return s.indexOf('{{') < 0 ? s : s.replace(BURACO, (_, e: string) => comoTexto(valor(e, esc)));
}

function valorDoAtributo(s: string | null, esc: Escopo): unknown {
  if (s == null) return undefined;
  const m = BURACO_INTEIRO.exec(s);
  return m ? valor(m[1]!, esc) : interpolar(s, esc);
}

function desenhar(origem: Node, esc: Escopo, destino: Node, registrar: (fn: Manipulador) => string) {
  const doc = destino.ownerDocument || document;
  for (let n = origem.firstChild; n; n = n.nextSibling) {
    if (n.nodeType === 3) {
      destino.appendChild(doc.createTextNode(interpolar((n as Text).data, esc)));
      continue;
    }
    if (n.nodeType !== 1) continue; // comentários ficam de fora
    const el = n as Element;
    const tag = el.localName;
    if (tag === 'sc-if') {
      if (valorDoAtributo(el.getAttribute('value'), esc)) desenhar(el, esc, destino, registrar);
      continue;
    }
    if (tag === 'sc-for') {
      const lista = valorDoAtributo(el.getAttribute('list'), esc);
      const como = el.getAttribute('as') || 'item';
      if (Array.isArray(lista)) {
        lista.forEach((item, i) => desenhar(el, Object.assign({}, esc, { [como]: item, [como + 'Index']: i }), destino, registrar));
      }
      continue;
    }
    const novo = el.namespaceURI === SVG ? doc.createElementNS(SVG, tag) : doc.createElement(tag);
    for (let i = 0; i < el.attributes.length; i++) {
      const a = el.attributes[i]!;
      const nome = a.name;
      if (nome.startsWith('hint-')) continue;
      const evento = EVENTOS[nome.toLowerCase()];
      if (evento) {
        const fn = valorDoAtributo(a.value, esc);
        if (typeof fn === 'function') novo.setAttribute('data-on-' + evento, registrar(fn as Manipulador));
        continue;
      }
      const v = valorDoAtributo(a.value, esc);
      if (v === false || v == null) continue;
      const texto = v === true ? '' : typeof v === 'string' ? v : comoTexto(v);
      if (a.namespaceURI) novo.setAttributeNS(a.namespaceURI, nome, texto);
      else novo.setAttribute(nome, texto);
    }
    desenhar(el, esc, novo, registrar);
    destino.appendChild(novo);
  }
}

export interface Montagem<T extends ComponenteDC> {
  instancia: T;
  desenharAgora(): void;
  desmontar(): void;
}

/**
 * Monta o componente dentro de `raiz` (o ShadowRoot do widget).
 * `molde` é o HTML da tela (sem helmet e sem script).
 */
export function montar<T extends ComponenteDC>(
  Classe: new (props: Record<string, any>) => T,
  raiz: ShadowRoot,
  molde: string,
  props: Record<string, any>,
  aoErrar: (e: unknown) => void = (e) => console.error('[Safe House]', e),
): Montagem<T> {
  const tpl = document.createElement('template');
  tpl.innerHTML = molde;
  const recipiente = document.createElement('div');
  recipiente.className = 'sh-raiz';
  raiz.appendChild(recipiente);

  const inst = new Classe(props);
  let mapa = new Map<string, Manipulador>();
  let agendado = false;
  let vivo = true;

  const desenharAgora = () => {
    agendado = false;
    if (!vivo) return;
    try {
      const novoMapa = new Map<string, Manipulador>();
      let n = 0;
      const registrar = (fn: Manipulador) => {
        const id = 'h' + n++;
        novoMapa.set(id, fn);
        return id;
      };
      const novo = document.createElement('div');
      desenhar(tpl.content, inst.renderVals(), novo, registrar);
      morphdom(recipiente, novo, { childrenOnly: true });
      mapa = novoMapa;
    } catch (e) {
      aoErrar(e);
    }
  };
  inst.__agendar = () => {
    if (!agendado) {
      agendado = true;
      queueMicrotask(desenharAgora);
    }
  };

  // Delegação: o evento sobe do alvo até a raiz chamando cada manipulador encontrado (como no React).
  const tipos = Array.from(new Set(Object.values(EVENTOS)));
  const ouvinte = (e: Event) => {
    const attr = 'data-on-' + e.type;
    let parar = false;
    const original = e.stopPropagation.bind(e);
    e.stopPropagation = () => {
      parar = true;
      original();
    };
    for (const no of e.composedPath()) {
      if (no === raiz || parar) break;
      if (no instanceof Element && no.hasAttribute(attr)) {
        const fn = mapa.get(no.getAttribute(attr)!);
        if (fn) {
          try {
            fn(e);
          } catch (err) {
            aoErrar(err);
          }
        }
      }
    }
  };
  for (const t of tipos) raiz.addEventListener(t, ouvinte);

  desenharAgora();
  try {
    inst.componentDidMount?.();
  } catch (e) {
    aoErrar(e);
  }

  return {
    instancia: inst,
    desenharAgora,
    desmontar() {
      vivo = false;
      for (const t of tipos) raiz.removeEventListener(t, ouvinte);
      try {
        inst.componentWillUnmount?.();
      } catch (e) {
        aoErrar(e);
      }
      recipiente.remove();
    },
  };
}
